import { match } from 'ts-pattern';
import type { Translate } from 'next-translate';

import {
  isPublicGameWithTeams,
  type PublicGame,
} from '~/domains/contest/domains/services/interfaces/propsService/api/game';
import type { PublicTeam } from '~/domains/contest/domains/services/interfaces/propsService/api/team';
import { formatEventDate } from '~/domains/market/utils/formatEventDate';
import {
  isMarketSubCategory,
  type MarketSubCategory,
  type PublicPredictionMarket,
} from '~/domains/market/services/interfaces/market.types';
import type { TeamType } from '~/domains/market/state/marketSelection.store';
import { withMarketLineUnit } from '~/domains/market/utils/marketLineUnit';
import { generateFutureSelectionTitle } from '~/domains/market/utils/selectionTitle';

import type { PublicPosition } from '../services/interfaces/positions.types';

export type PositionDisplay = {
  title: string;
  subtitle: string;
  teamName: string;
  teamType: TeamType;
  teamPrimaryColor?: string;
  teamIconUrl?: string;
  marketType?: PublicPredictionMarket['type'];
  league?: MarketSubCategory;
};

type PositionSelectionDisplay = Pick<
  PositionDisplay,
  'title' | 'teamName' | 'teamType' | 'teamPrimaryColor' | 'teamIconUrl' | 'marketType'
>;

export type PositionEntities = {
  event?: PublicGame;
  homeTeam?: PublicTeam;
  awayTeam?: PublicTeam;
  futureTeam?: PublicTeam;
};

export function getPositionEventId(
  position: PublicPosition | null | undefined
): string | undefined {
  if (!position || position.market.type === 'future_team_champion') return undefined;
  return position.market.event_id;
}

export function getPositionFutureTeamId(
  position: PublicPosition | null | undefined
): string | undefined {
  if (!position || position.market.type !== 'future_team_champion') return undefined;
  return position.market.team_id;
}

export function resolvePositionEntities(
  position: PublicPosition,
  eventsById: Map<string, PublicGame>,
  teamsById: Map<string, PublicTeam>
): PositionEntities {
  const futureTeamId = getPositionFutureTeamId(position);
  if (futureTeamId) return { futureTeam: teamsById.get(futureTeamId) };

  const eventId = getPositionEventId(position);
  const event = eventId ? eventsById.get(eventId) : undefined;

  if (!event || !isPublicGameWithTeams(event)) return { event };

  return {
    event,
    homeTeam: teamsById.get(event.home.id),
    awayTeam: teamsById.get(event.away.id),
  };
}

export function getPositionDisplay(
  position: PublicPosition,
  { event, homeTeam, awayTeam, futureTeam }: PositionEntities,
  t: Translate
): PositionDisplay | null {
  const { market, side } = position;

  if (market.type === 'future_team_champion') {
    const selection = side === 'yes' ? market.yes_line_display : market.no_line_display;
    const futureTeamName = futureTeam?.name ?? futureTeam?.alias;

    return {
      title: generateFutureSelectionTitle(market, side, futureTeam),
      subtitle: market.title,
      teamName: futureTeamName ?? selection,
      teamType: 'home',
      teamPrimaryColor: futureTeam?.primary_color,
      teamIconUrl: futureTeam?.icon_url,
      marketType: market.type,
      league: market.sub_category,
    };
  }

  if (!event || !isPublicGameWithTeams(event)) return null;

  const getTeam = (teamId: string) => (teamId === event.home.id ? homeTeam : awayTeam);
  const getTeamName = (teamId: string) =>
    getTeam(teamId)?.name ?? (teamId === event.home.id ? event.home.alias : event.away.alias);
  const getTeamType = (teamId: string): TeamType => (teamId === event.home.id ? 'home' : 'away');

  const selection = match(market)
    .returnType<PositionSelectionDisplay>()
    .with({ type: 'moneyline' }, (moneyline) => {
      const teamId = match(moneyline)
        .with({ moneyline_type: 'two_way' }, (twoWay) =>
          side === 'yes' ? twoWay.yes_team_id : twoWay.no_team_id
        )
        .otherwise((oneWay) => oneWay.team_id);
      const teamName = getTeamName(teamId);

      return {
        title: t('selection.moneyline', { team: teamName }),
        teamName,
        teamType: getTeamType(teamId),
        teamPrimaryColor: getTeam(teamId)?.primary_color,
        teamIconUrl: getTeam(teamId)?.icon_url,
        marketType: moneyline.type,
      };
    })
    .with({ type: 'spread' }, (spread) => {
      const teamId = side === 'yes' ? spread.yes_team_id : spread.no_team_id;
      const teamName = getTeamName(teamId);
      const lineDisplay = side === 'yes' ? spread.yes_line_display : spread.no_line_display;

      return {
        title: t('selection.spread', {
          team: teamName,
          line: withMarketLineUnit(lineDisplay, spread.sub_category, t),
        }),
        teamName,
        teamType: getTeamType(teamId),
        teamPrimaryColor: getTeam(teamId)?.primary_color,
        teamIconUrl: getTeam(teamId)?.icon_url,
        marketType: spread.type,
      };
    })
    .with({ type: 'total' }, (total) => {
      const isOver = side === 'yes';
      const lineDisplay = isOver ? total.yes_line_display : total.no_line_display;

      return {
        title: t('selection.total', {
          line: withMarketLineUnit(lineDisplay, total.sub_category, t),
        }),
        teamName: isOver ? t('selection.over') : t('selection.under'),
        teamType: isOver ? 'away' : 'home',
        teamPrimaryColor: homeTeam?.primary_color,
        marketType: total.type,
      };
    })
    .otherwise(() => ({ title: market.title, teamName: market.title, teamType: 'home' }));

  const homeAlias = homeTeam?.alias ?? event.home.alias;
  const awayAlias = awayTeam?.alias ?? event.away.alias;

  return {
    ...selection,
    subtitle: `${awayAlias} @ ${homeAlias} ${formatEventDate(event.start_date)}`,
    league: isMarketSubCategory(event.league) ? event.league : undefined,
  };
}
